/**
 * Central Color Schemes Configuration
 * ─────────────────────────────────────
 * Single source of truth for all accent color schemes in the application.
 *
 * To add a new color scheme:
 *   1. Add an entry to the COLOR_SCHEMES array below.
 *   2. Add matching CSS variable blocks in src/lib/styles/_variables.scss
 *      for [data-color-scheme="<value>"] and [data-theme="dark"][data-color-scheme="<value>"].
 *   That's it — Settings UI, startup hydration, MUI theme, and getAccentColor()
 *   all read from this file automatically.
 *
 * Fields:
 *   value     – Stored in localStorage & set as data-color-scheme attribute.
 *               The default scheme's value is used when no attribute is set (:root).
 *   label     – Human-readable name shown in the Settings UI.
 *   color     – Light-mode accent hex (used for swatch preview & MUI primary).
 *   darkColor – Dark-mode accent hex (optional, falls back to `color`).
 *   isDefault – Exactly ONE entry should have this set to true.
 */

import { getLocalStorage, LOCAL_STORAGE_KEYS } from "../utils/localStorageUtil";

const COLOR_SCHEMES = [
  { value: "teal", label: "Teal Green", color: "#0d9488", darkColor: "#14b8a6", isDefault: true },
  { value: "orange", label: "Enphase Orange", color: "#f26722", darkColor: "#f47c20" },
  { value: "blue", label: "Ocean Blue", color: "#2563eb", darkColor: "#3b82f6" },
  { value: "indigo", label: "Indigo", color: "#6366f1", darkColor: "#818cf8" },
];

/** The default scheme object (the one with isDefault: true). */
export const DEFAULT_SCHEME = COLOR_SCHEMES.find(s => s.isDefault) || COLOR_SCHEMES[0];

/**
 * Retrieve the saved color scheme value from localStorage, falling back to the default.
 * @returns {string} The scheme value (e.g. "teal", "blue").
 */
export const getSavedColorScheme = () => {
  try {
    return getLocalStorage(LOCAL_STORAGE_KEYS.COLOR_SCHEME, false) || DEFAULT_SCHEME.value;
  } catch {
    return DEFAULT_SCHEME.value;
  }
};

/**
 * Apply a color scheme to the document.
 * Sets or removes the data-color-scheme attribute on <html>.
 * @param {string} schemeValue – The scheme value to apply.
 */
export const applyColorScheme = schemeValue => {
  if (schemeValue === DEFAULT_SCHEME.value) {
    document.documentElement.removeAttribute("data-color-scheme");
  } else {
    document.documentElement.setAttribute("data-color-scheme", schemeValue);
  }
};

/**
 * Look up a scheme object by its value.
 * @param {string} value
 * @returns {object|undefined}
 */
export const getSchemeByValue = value => COLOR_SCHEMES.find(s => s.value === value);

export default COLOR_SCHEMES;
